import { defineStore } from 'pinia'
import cloneDeep from 'lodash/cloneDeep'
import apolloProvider from '@/apollo'
import {
  QUERY_GEO_FENCES,
  QUERY_GEO_FENCE,
  QUERY_GEO_FENCE_ACTIVITY,
  QUERY_GEO_FENCE_CATEGORIES,
  QUERY_GEO_FENCE_UNIT_LINKS,
  QUERY_ELIGIBLE_GEO_FENCES,
} from '@/modules/cercas-virtuais/graphql/queries'
import {
  MUTATION_UPSERT_GEO_FENCE,
  MUTATION_UPSERT_GEO_FENCE_DRAFT,
  MUTATION_SET_GEO_FENCE_STATUS,
  MUTATION_DELETE_GEO_FENCE,
  MUTATION_UPSERT_GEO_FENCE_CATEGORY,
  MUTATION_DELETE_GEO_FENCE_CATEGORY,
} from '@/modules/cercas-virtuais/graphql/mutations'
import { orderByStatusGroup } from '@/modules/cercas-virtuais/helpers/sortMap'
import { temLocalizacao, ocupaAreaAtiva, estaAtiva, cercasParaFoco } from '@/modules/cercas-virtuais/helpers/geofence'
import { paginaComIndice } from '@/modules/cercas-virtuais/helpers/listaScroll'

const client = () => apolloProvider.defaultClient

// Substitui/insere uma cerca na lista pelo id (após upsert/setStatus).
const upsertInList = (list, fence) => {
  const index = list.findIndex((f) => f.id === fence.id)
  if (index === -1) return [fence, ...list]
  const next = [...list]
  next[index] = fence
  return next
}

export const useCercasStore = defineStore('cercas', {
  state: () => ({
    fences: [],
    // Geometria das cercas que definem o FOCO DEFAULT do mapa: até 10 ATIVAS com localização
    // (MK-21210), snapshot de uma carga SEM filtro server-side. Não dá pra derivar de `fences`
    // porque a busca é server-side, ela SUBSTITUI a lista: sem o snapshot, uma busca sem resultado
    // deixaria o mapa num ponto arbitrário (demo 2026-07-24).
    focusFences: [],
    // Vínculos loja↔cerca para o alerta do formulário (MK-21580): { id, name, associatedUnits }.
    // Snapshot de uma carga SEM filtro de servidor, pelo mesmo motivo do `focusFences` — `fences`
    // carrega com o status/busca da listagem, e alerta que depende disso esconde vínculo com cerca
    // Inativa ou Expirada.
    unitLinks: [],
    selectedFence: null,
    categories: [],
    // filtros (UI → ListGeoFencesInput)
    searchText: '',
    statusFilter: [], // GeoFenceStatus[]
    categoryFilter: [], // ID[] (multi: aplicado client-side; contrato aceita 1 categoryId)
    // Sem `sortField`/`sortDirection` no estado: a listagem não ordena, quem ordena é o backend
    // (MK-21222 — o porquê está no `buildListInput`).
    // paginação client-side (10/página, scroll infinito; sem paginação server-side)
    page: 1,
    pageSize: 10,
    loading: false,
    loadingDetail: false,
    // Loading só da ATIVIDADE (MK-21176): a cobertura não é refeita por troca de período, então ela
    // não pode entrar em estado de carregamento junto — era isso que fazia o card de Cobertura
    // branquear e a barra reanimar de zero a cada troca de filtro, o que a olho é indistinguível de
    // "a cobertura foi recalculada".
    loadingMetrics: false,
    // Contador monotônico de requisições de atividade, para descartar resposta fora de ordem.
    metricsSeq: 0,
    saving: false,
    error: null,
    // Consumida uma única vez pelo Index ao remontar, para distinguir "voltei do detalhe/nova"
    // (restaura o default) de qualquer outro remount da listagem (preserva o que a store tem).
    returnFromDetail: false,
  }),
  getters: {
    // Filtro de status + categoria (multi) no cliente. REGRA (Bruno): SEM filtro de status,
    // Inativa/Expirada NÃO aparecem por padrão — a listagem mostra só Rascunho + Ativa. Quando o
    // usuário escolhe status no filtro, mostra exatamente o que foi selecionado (incl. Inativa/Expirada).
    filteredFences: (state) => {
      const statusSet = state.statusFilter.length ? new Set(state.statusFilter) : new Set(['DRAFT', 'ACTIVE'])
      const catSet = state.categoryFilter.length ? new Set(state.categoryFilter) : null
      return state.fences.filter((f) => {
        if (!statusSet.has(f.status)) return false
        if (catSet && !(f.category && catSet.has(f.category.id))) return false
        return true
      })
    },
    // Rascunho > Ativa > Inativa > Expirada (RFC-195), mantendo a ordem relativa vinda do
    // backend (ENTRIES DESC) dentro de cada grupo — o schema não tem sort nativo por status,
    // então o agrupamento é client-side. A regra vive em `orderByStatusGroup`, que tem spec:
    // separar só Rascunho do resto deixava Inativa e Expirada intercaladas entre si.
    sortedFences() {
      return orderByStatusGroup(this.filteredFences)
    },
    // Fatia para o scroll infinito (10/página em memória).
    visibleFences() {
      return this.sortedFences.slice(0, this.page * this.pageSize)
    },
    hasMorePages() {
      return this.visibleFences.length < this.filteredFences.length
    },
    totalCount() {
      return this.filteredFences.length
    },
    hasFences: (state) => state.fences.length > 0,
    getById: (state) => (id) => state.fences.find((f) => f.id === id) || null,
    /**
     * Quem o mapa da LISTAGEM DESENHA: espelha exatamente o que a listagem exibe, inclusive Inativa
     * e Expirada (MK-21205). O único corte é ter coordenada.
     *
     * Antes o mapa usava `mapFences`, que re-filtra por status sobre um `filteredFences` que já
     * respeitou o filtro do usuário. O efeito era: filtrar por Inativa mostrava as linhas na lista e
     * deixava o mapa EM BRANCO, ainda enquadrado nas Ativas. É o defeito real do card, que descreve
     * o sintoma como "Inativa/Expirada não aparecem no mapa".
     *
     * "Quem desenha" e "quem conflita" são perguntas diferentes desde este card: participação no
     * conflito continua sendo `mapFences` (Ativa + Rascunho), passada ao mapa em separado.
     */
    mapRenderFences() {
      return this.filteredFences.filter(temLocalizacao)
    },
    // Quem PARTICIPA do conflito no mapa da listagem: só ACTIVE + DRAFT com localização. Também é a
    // lista que o DETALHE usa pro banner e pro contexto, então mexer aqui vaza pra fora da listagem.
    // `temLocalizacao` em vez de checar so o objeto `location`: com `{ location: {} }` a cerca
    // passava o getter e era descartada depois, no mapa. Aqui isso quebraria a invariante do
    // `conflictFences` (ser subconjunto do que e renderizado), porque os dois getters usavam cortes
    // diferentes. Achado do Copilot na #561.
    mapFences() {
      return this.filteredFences.filter((f) => temLocalizacao(f) && ocupaAreaAtiva(f))
    },
    /**
     * Referência das telas de CRIAÇÃO e EDIÇÃO: só cercas ATIVAS.
     *
     * Só Ativa (MK-21209): ali o mapa serve pra posicionar uma cerca, e rascunho de terceiro não está
     * em operação — poluía a referência. A listagem tem regra própria e mais ampla (`mapFences` e
     * `mapRenderFences`), porque lá o objetivo é mostrar o que existe, não o que opera.
     *
     * Lê `state.fences` e não `filteredFences`, então escapa do filtro de CATEGORIA da listagem, que é
     * estado de UI vivendo no mesmo store singleton: com `mapFences` aqui, categoria marcada na
     * listagem escondia conflito real (QA §3.8).
     *
     * CUIDADO, e é o limite real deste getter: STATUS e BUSCA são filtros de SERVIDOR e recortam o
     * próprio `state.fences`, então continuam contaminando. Filtrar a listagem por Inativa e clicar em
     * "Nova cerca virtual" deixa esta lista vazia, sem cerca de contexto e sem conflito. É
     * pré-existente e está declarado no MK-21209; consertar exige buscar as Ativas sem os filtros da
     * tela, ou seja uma requisição a mais. Não escreva "ignora os filtros da listagem": não ignora.
     *
     * Antes chamava `allMapFences`; o nome novo diz de qual tela é a regra, porque agora existem duas.
     */
    editMapFences: (state) => state.fences.filter((f) => temLocalizacao(f) && estaAtiva(f)),
    categoriesForSelect: (state) => state.categories.map((c) => ({ value: c.id, label: c.name, color: c.color, isStore: c.isStore })),
    isStoreCategory: (state) => (id) => {
      const cat = state.categories.find((c) => c.id === id)
      return !!(cat && cat.isStore)
    },
  },
  actions: {
    /**
     * Input da listagem: filtros SIM, ordenação NÃO (MK-21222).
     *
     * A listagem não tem UI de ordenação, e mesmo assim o front mandava `sort: ['ENTRIES']` fixo. No
     * `geofence-microservice` o sort do input **substitui** o default inteiro
     * (`geo-fence-list.query.ts`: `DEFAULT_GEO_FENCE_LIST_SORT = { draft: -1, 'cachedMetrics.entries':
     * -1, _id: -1 }` só é usado quando `sort` vem vazio), então pedir ENTRIES derrubava junto a
     * prioridade de Rascunho e o desempate por data — e o rascunho recém-criado ia parar no fim da
     * lista, que é o bug do card.
     *
     * Ordenação é regra de negócio e vive no backend. `buildSortInput` continua em `helpers/sortMap`
     * para quando existir a UI de ordenar; até lá, o input não leva sort nenhum.
     */
    buildListInput() {
      const input = {}
      if (this.statusFilter.length) input.status = this.statusFilter
      if (this.searchText && this.searchText.trim()) input.search = this.searchText.trim()
      return input
    },
    async fetchCercas() {
      this.loading = true
      this.error = null
      try {
        const { data } = await client().query({
          query: QUERY_GEO_FENCES,
          variables: { input: this.buildListInput() },
          fetchPolicy: 'no-cache',
        })
        // geoFences responde ListGeoFencesResponse (geofence-microservice#16): { geoFences, records, page }.
        this.fences = cloneDeep(data?.geoFences?.geoFences ?? [])
        this.page = 1
        // Baseline do foco: só quando a consulta foi SEM filtro server-side (busca/status). A partir
        // daí, uma busca sem resultado ainda tem para onde enquadrar o mapa.
        //
        // Até 10 ATIVAS (MK-21210). Antes eram TODAS as Ativa+Rascunho, e numa conta com muito
        // rascunho disperso o mapa abria com zoom amplo demais, centrado em região que não opera.
        // O filtro de status acontece AQUI, então o snapshot guarda só o que enquadrar precisa
        // (coordenada). Não reponha o `status`: quem consome é o `fitToFences`, que não olha status,
        // e um campo a mais no estado só convida alguém a re-filtrar o que já está filtrado.
        if (!this.searchText.trim() && !this.statusFilter.length) {
          this.focusFences = cercasParaFoco(this.fences).map((f) => ({ id: f.id, location: f.location, radiusMeters: f.radiusMeters }))
        }
      } catch (error) {
        this.error = error
        this.fences = []
        throw error
      } finally {
        this.loading = false
      }
    },
    async fetchCategorias() {
      const { data } = await client().query({
        query: QUERY_GEO_FENCE_CATEGORIES,
        fetchPolicy: 'no-cache',
      })
      this.categories = cloneDeep(data?.geoFenceCategories ?? [])
      return this.categories
    },
    /**
     * Carrega os vínculos loja↔cerca do account (MK-21580).
     *
     * Não mexe em `fences` de propósito: a listagem é dona daquele estado, com os filtros dela. Falha
     * aqui não pode derrubar o formulário — sem os vínculos o alerta simplesmente não aparece, que é
     * o comportamento de antes deste card.
     */
    async fetchUnitLinks() {
      const { data } = await client().query({
        query: QUERY_GEO_FENCE_UNIT_LINKS,
        fetchPolicy: 'no-cache',
      })
      this.unitLinks = cloneDeep(data?.geoFences?.geoFences ?? [])
      return this.unitLinks
    },
    async fetchDetalhe(id, period) {
      this.loadingDetail = true
      this.error = null
      try {
        // errorPolicy 'all': um erro isolado no sub-campo `metrics` (ex.: tabela de eventos do
        // ClickHouse ainda não provisionada pro tenant) NÃO deve derrubar o detalhe inteiro —
        // a config carrega normalmente e as métricas degradam pra vazio. Sem isso o Apollo
        // rejeitaria a query toda ('none') e a tela mostraria "Não foi possível carregar a cerca".
        const { data } = await client().query({
          query: QUERY_GEO_FENCE,
          variables: { id, metricsInput: period ? { period } : undefined },
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        })
        // Só é falha real se o próprio nó geoFence não veio (ID inexistente / erro de topo).
        if (!data?.geoFence) throw new Error('Cerca não encontrada')
        this.selectedFence = cloneDeep(data.geoFence)
        return this.selectedFence
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.loadingDetail = false
      }
    },
    /**
     * Troca de período: refaz SÓ a atividade (MK-21176).
     *
     * Antes esta action pedia o bloco inteiro de métricas e fazia
     * `this.selectedFence = { ...this.selectedFence, metrics }`, ou seja substituía o objeto
     * `metrics` INTEIRO. Como cobertura e atividade viviam nesse mesmo objeto, cada troca de filtro
     * reescrevia os campos de cobertura com o payload do novo período — e o serviço serve 30 dias do
     * cache de 2h e 60/90 ao vivo, então o número de fato mudava. Agora `cobertura` só é escrita
     * pelo `fetchDetalhe`, e o período não a alcança.
     */
    async fetchMetrics(id, period) {
      // Token de sequência. O guard antigo era só `selectedFence.id === id`, que compara a CERCA e
      // não o período: uma resposta de 60 dias que chegasse atrasada era escrita mesmo com o
      // seletor já em 90, e o `finally` zerava o loading na primeira resposta a voltar, não na
      // última disparada. Nos dois casos o usuário vê número trocando sozinho.
      this.metricsSeq += 1
      const seq = this.metricsSeq
      this.loadingMetrics = true
      try {
        const { data } = await client().query({
          query: QUERY_GEO_FENCE_ACTIVITY,
          variables: { id, metricsInput: period ? { period } : undefined },
          fetchPolicy: 'no-cache',
          // Mesma tolerância do detalhe: falha nas métricas degrada pra "sem dados", não quebra.
          errorPolicy: 'all',
        })
        const atividade = data?.geoFence?.atividade ? cloneDeep(data.geoFence.atividade) : null
        // Resposta obsoleta não escreve: quem manda é a última troca que o usuário fez.
        if (seq !== this.metricsSeq) return atividade
        if (this.selectedFence && this.selectedFence.id === id) {
          this.selectedFence = { ...this.selectedFence, atividade }
        }
        return atividade
      } finally {
        // Só a requisição mais recente apaga o loading, senão uma resposta antiga chegando primeiro
        // desligaria o indicador com a busca nova ainda em voo.
        if (seq === this.metricsSeq) this.loadingMetrics = false
      }
    },
    // Foco inicial do mapa (top-N por proximidade). reference obrigatória no contrato.
    async fetchEligible(reference) {
      const { data } = await client().query({
        query: QUERY_ELIGIBLE_GEO_FENCES,
        variables: { reference },
        fetchPolicy: 'no-cache',
      })
      return cloneDeep(data?.eligibleGeoFences ?? [])
    },
    async upsert(input) {
      if (this.saving) return null // trava double-click (QA §15.5)
      this.saving = true
      try {
        const { data } = await client().mutate({ mutation: MUTATION_UPSERT_GEO_FENCE, variables: { input } })
        const fence = data?.upsertGeoFence
        if (fence) {
          this.fences = upsertInList(this.fences, cloneDeep(fence))
          this.selectedFence = cloneDeep(fence)
        }
        return fence
      } finally {
        this.saving = false
      }
    },
    async upsertDraft(input) {
      if (this.saving) return null
      this.saving = true
      try {
        const { data } = await client().mutate({ mutation: MUTATION_UPSERT_GEO_FENCE_DRAFT, variables: { input } })
        const fence = data?.upsertGeoFenceDraft
        if (fence) {
          this.fences = upsertInList(this.fences, cloneDeep(fence))
          this.selectedFence = cloneDeep(fence)
        }
        return fence
      } finally {
        this.saving = false
      }
    },
    async setStatus(id, status) {
      if (this.saving) return null
      this.saving = true
      try {
        const { data } = await client().mutate({ mutation: MUTATION_SET_GEO_FENCE_STATUS, variables: { id, status } })
        const fence = data?.setGeoFenceStatus
        if (fence) {
          this.fences = upsertInList(this.fences, cloneDeep(fence))
          if (this.selectedFence && this.selectedFence.id === id) this.selectedFence = { ...this.selectedFence, ...cloneDeep(fence) }
        }
        return fence
      } finally {
        this.saving = false
      }
    },
    async remove(id) {
      if (this.saving) return false
      this.saving = true
      try {
        const { data } = await client().mutate({ mutation: MUTATION_DELETE_GEO_FENCE, variables: { id } })
        const ok = !!data?.deleteGeoFence
        if (ok) {
          this.fences = this.fences.filter((f) => f.id !== id)
          if (this.selectedFence && this.selectedFence.id === id) this.selectedFence = null
        }
        return ok
      } finally {
        this.saving = false
      }
    },
    async upsertCategoria(input) {
      const { data } = await client().mutate({ mutation: MUTATION_UPSERT_GEO_FENCE_CATEGORY, variables: { input } })
      await this.fetchCategorias()
      return data?.upsertGeoFenceCategory
    },
    // Retorna { ok, blocked } — backend responde false quando há cercas vinculadas.
    async removeCategoria(id) {
      const { data } = await client().mutate({ mutation: MUTATION_DELETE_GEO_FENCE_CATEGORY, variables: { id } })
      const ok = !!data?.deleteGeoFenceCategory
      if (ok) await this.fetchCategorias()
      return { ok, blocked: !ok }
    },
    // ─── filtros/paginação ───────────────────────────────────────────────
    setSearchText(value) {
      this.searchText = value || ''
    },
    setStatusFilter(values) {
      this.statusFilter = Array.isArray(values) ? values : []
      this.page = 1
    },
    setCategoryFilter(values) {
      this.categoryFilter = Array.isArray(values) ? values : []
      this.page = 1
    },
    resetFilters() {
      this.searchText = ''
      this.statusFilter = []
      this.categoryFilter = []
      this.page = 1
    },
    markReturnFromDetail() {
      this.returnFromDetail = true
    },
    consumeReturnFromDetail() {
      const pending = this.returnFromDetail
      this.returnFromDetail = false
      return pending
    },
    // Restaura listagem padrão ao sair do detalhe (store + sinalização para refs locais do Index).
    prepareListRestore() {
      this.markReturnFromDetail()
      this.resetFilters()
    },
    nextPage() {
      if (this.hasMorePages) this.page += 1
    },
    /**
     * Garante que a cerca `id` está na fatia RENDERIZADA da lista (MK-21221), levantando `page` até
     * cobrir o índice dela. Devolve `false` quando a cerca não está na listagem atual.
     *
     * Precisa existir porque a paginação é client-side: a cerca pode estar em `fences` e mesmo assim
     * não ter linha no DOM, e aí não há o que rolar até. Clicar num pin do mapa alcança exatamente
     * esse caso, porque o mapa desenha TODAS as cercas filtradas, não só as 10 da página atual.
     *
     * Só sobe, nunca desce: baixar `page` desrenderizaria linhas que o usuário já rolou.
     */
    revelarNaLista(id) {
      const indice = this.sortedFences.findIndex((f) => f.id === id)
      if (indice === -1) return false
      const alvo = paginaComIndice(indice, this.pageSize)
      if (alvo > this.page) this.page = alvo
      return true
    },
  },
})
